import withStyles from '@mui/styles/withStyles';
import React, { Fragment, PureComponent } from 'react';
import Grid from '../form/Grid';
import BiggerMessageBar from '../BiggerMessageBar';
import { InlineHeader } from '../form/Inline';
import Checkbox from '../form/Checkbox';
import { AltButton } from '../form/PrimaryButton';
import PayableIcon from '../icon/PayableIcon';
import QuotesIcon from '../icon/QuotesIcon';
import { getClient } from '../../apollo';
import gql from 'graphql-tag';
import Spinner from '../Spinner';
import { isRelatedObjectDefined } from '../../util/bookable';
import PurchaseOrderModal, {
    createBookingPurchaseOrderMutation,
    createNewBookingPurchaseOrderMutation
} from './PurchaseOrderModal';
import { withSnackbarMessage } from '../../context/SnackbarMessage';
import { compose } from '../../reactApolloCompat';
import PurchaseInvoiceModal from './PurchaseInvoiceModal';
import { niceDateFromString, niceDateTimeFromString } from '../../util/date';
import { joinDefined } from '../../util/strings';

const sendRequestCM = gql`
    mutation CreateCremationRequest($input: UpdateFuneralInput!) {
        updateFuneral(input: $input) {
            ID
            CremationOrder {
                ID
                CremationPending
            }
        }
    }
`;

class CremationRequest extends PureComponent {
    state = {
        loading: false,
        orderModalOpen: false
    };

    sendMVCRequestMutation = async input => {
        const asyncQuery = await getClient().mutate({ mutation: sendRequestCM, variables: { input } });
        return asyncQuery.data;
    };

    handleSendMVC = e => {
        if (!!e) {
            e.preventDefault();
            e.stopPropagation();
        }
        const response = window.confirm('Are you sure you want to request a cremation?');
        if (response) this.sendMVC();
    };

    sendMVC = () => {
        const { form } = this.props;
        const input = {
            ID: form.getField('ID'),
            CremationOrder: {
                CremationPending: true
            }
        };
        const me = this;
        me.setState({ loading: true });
        const onError = e => {
            console.error('error occurred saving CM request', e);
            me.setState({ loading: false });
            me.props.setSnackbarMessage(
                'Oops, there was an error - your changes have NOT saved.',
                false,
                null,
                new Error(e)
            );
        };
        this.sendMVCRequestMutation(input).then(data => {
            if (data && data.updateFuneral) {
                form.doRefetch(() => me.setState({ loading: false }));
            } else {
                onError('no data returned!');
            }
        }, onError);
    };

    render() {
        const { form, locked, defaultCrematorium, children } = this.props;
        if (!(defaultCrematorium && defaultCrematorium.ID > 0)) return null;
        const { loading, orderModalOpen } = this.state;
        const requested = form.getField('CremationOrder.ID') > 0;
        const confirmed = form.getField('Disposal.Confirmed');
        const deliveryHappened = !!form.getField('CremationOrder.DeliveryDate');
        const deliveryTimestamp =
            deliveryHappened &&
            niceDateTimeFromString(
                joinDefined(
                    [form.getField('CremationOrder.DeliveryDate'), form.getField('CremationOrder.DeliveryTime')],
                    ' '
                )
            );
        const cremationHappened = form.getField('CremationOrder.CremationNumber') > 0;
        const insertionDate = form.getField('CremationOrder.InsertionDate');
        const disabledBusy = form.isDirty || loading;
        const labelSupplier = 'Cremation';
        const items = form.getField('Disposal.Bookings') || [];
        const hasBooking = items.find(
            obj => !obj.Cancelled && !!obj.Contact && defaultCrematorium.ID === (obj.Contact.ID || obj.ContactID)
        );
        const booking = hasBooking || {
            // PurchaseOrderModal will attach this to funeral
            Cancelled: false,
            Confirmed: !!confirmed,
            Contact: defaultCrematorium
        };
        const hasPurchaseOrder = booking.PurchaseOrder && 0 < booking.PurchaseOrder.ID ? booking.PurchaseOrder : false;
        return (
            <>
                <Grid item pc={1}>
                    <InlineHeader header="Same-day Cremation">
                        <Checkbox disabled={locked} form={form} name={'Disposal.SameDayCremation'} label={'Required'} />
                    </InlineHeader>
                </Grid>
                {children}
                <Grid item pc={1}>
                    {!requested && confirmed ? ( // legacy data, no orders
                        <BiggerMessageBar messageType={'good'}>
                            The booking with {defaultCrematorium.Name} has been confirmed.
                        </BiggerMessageBar>
                    ) : !!cremationHappened ? (
                        <BiggerMessageBar messageType="good">
                            {defaultCrematorium.Name} confirms that the deceased was cremated on{' '}
                            {niceDateFromString(insertionDate)}.
                        </BiggerMessageBar>
                    ) : (
                        <>
                            {!!deliveryHappened ? (
                                <BiggerMessageBar messageType="good">
                                    {defaultCrematorium.Name} confirms that the deceased was delivered at{' '}
                                    {deliveryTimestamp}.
                                </BiggerMessageBar>
                            ) : (
                                (!requested && (
                                    <BiggerMessageBar messageType={'warning'}>
                                        {defaultCrematorium.Name} will need to accept this booking. Please{' '}
                                        {form.isDirty && 'save and then'} press 'Send Cremation Request' when ready.
                                    </BiggerMessageBar>
                                )) ||
                                (!confirmed && (
                                    <BiggerMessageBar>
                                        Cremation request #{form.getField('CremationOrder.ID')} was sent to{' '}
                                        {defaultCrematorium.Name}. They should accept this request before you create a
                                        bill.
                                    </BiggerMessageBar>
                                )) || (
                                    <BiggerMessageBar messageType={'good'}>
                                        The booking with {defaultCrematorium.Name} has been confirmed.
                                    </BiggerMessageBar>
                                )
                            )}
                        </>
                    )}
                    <InlineHeader header={'Confirmation status'}>
                        <Checkbox form={form} name={'Disposal.Confirmed'} label={'Booking Confirmed'} disabled />
                    </InlineHeader>
                    <div style={{ float: 'right' }}>
                        {(!requested && !confirmed && (
                            <AltButton disabled={disabledBusy} onClick={this.handleSendMVC}>
                                {loading ? <Spinner /> : <PayableIcon />}
                                <span>Send Cremation Request</span>
                            </AltButton>
                        )) ||
                            (!(booking && isRelatedObjectDefined(booking, 'PurchaseOrder')) && (
                                <Fragment>
                                    <AltButton
                                        disabled={disabledBusy || !requested}
                                        onClick={() => this.setState({ orderModalOpen: true })}
                                    >
                                        <QuotesIcon />
                                        <span>Create Bill...</span>
                                    </AltButton>
                                    <PurchaseOrderModal
                                        canEdit={true}
                                        canAdd={true}
                                        open={orderModalOpen}
                                        booking={booking}
                                        contact={defaultCrematorium}
                                        funeralID={form.getField('ID')}
                                        funeralKey={form.getField('LegacyKey')}
                                        eventObj={form.getField('Disposal')}
                                        label={labelSupplier}
                                        isOrder={false}
                                        purchaseOrderField="PurchaseOrder"
                                        mutation={
                                            booking && booking.ID > 0
                                                ? createBookingPurchaseOrderMutation
                                                : createNewBookingPurchaseOrderMutation
                                        }
                                        onClose={reload => {
                                            const me = this;
                                            if (reload && form.doRefetch)
                                                form.doRefetch(() => me.setState({ orderModalOpen: false }));
                                            else this.setState({ orderModalOpen: false });
                                        }}
                                    />
                                </Fragment>
                            )) || (
                                <PurchaseInvoiceModal
                                    hasPurchaseOrder={hasPurchaseOrder}
                                    labelSupplier={labelSupplier}
                                    form={form}
                                />
                            )}
                    </div>
                </Grid>
            </>
        );
    }
}

const styles = () => ({});

export default compose(withSnackbarMessage, withStyles(styles))(CremationRequest);
